<template>
  <div>
    <v-card>
      <v-card-title class="d-flex align-center pb-2">
        <v-icon color="error" class="me-2">
          mdi-alert-octagon
        </v-icon>
        <span class="text-h6 font-weight-bold">{{ $t('Emergency End Requests') }}</span>
      </v-card-title>

      <!-- Filters Section -->
      <v-card-text class="pt-2">
        <v-row align="center">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('search')"
              single-line
              hide-details
              dense
              outlined
              @input="loadRequests"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              :label="$t('Status')"
              dense
              outlined
              hide-details
              @change="loadRequests"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="12" md="4" class="text-right">
            <v-btn
              color="primary"
              outlined
              small
              @click="loadRequests"
              :loading="isLoading"
            >
              <v-icon small class="me-1">mdi-refresh</v-icon>
              {{ $t('refresh') || 'تحديث' }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="emergencyRequests"
        item-key="id"
        class="elevation-0"
      >
        <!-- Request ID -->
        <template v-slot:item.id="{ item }">
          <span class="font-weight-bold">#{{ item.id }}</span>
        </template>

        <!-- Driver / Captain -->
        <template v-slot:item.driver="{ item }">
          <div class="d-flex align-center py-2" v-if="item.driver">
            <v-avatar size="32" class="me-2">
              <v-img :src="getAvatar(item.driver.avatar)"></v-img>
            </v-avatar>
            <div>
              <div class="font-weight-bold">{{ item.driver.name }}</div>
              <small class="text-muted">{{ item.driver.tel_number || item.driver.email }}</small>
            </div>
          </div>
          <span v-else class="text-muted">-</span>
        </template>

        <!-- Planned Trip Info -->
        <template v-slot:item.planned_trip="{ item }">
          <div v-if="item.planned_trip">
            <div class="font-weight-bold">
              <v-icon small color="primary">mdi-bus</v-icon>
              {{ $t('trip') || 'رحلة' }} #{{ item.planned_trip.id }}
            </div>
            <small class="text-muted" v-if="item.planned_trip.route">
              {{ item.planned_trip.route.name }}
            </small>
            <div class="text-caption text-muted" v-if="item.planned_trip.planned_date">
              {{ item.planned_trip.planned_date }}
            </div>
          </div>
          <span v-else class="text-muted">#{{ item.planned_trip_id }}</span>
        </template>

        <!-- Reason -->
        <template v-slot:item.reason="{ item }">
          <v-chip small color="orange lighten-5" class="orange--text text--darken-3 font-weight-medium">
            <v-icon left small>mdi-alert-circle</v-icon>
            {{ getReasonLabel(item) }}
          </v-chip>
        </template>

        <!-- Note -->
        <template v-slot:item.note="{ item }">
          <span class="text-truncate d-inline-block" style="max-width: 200px;">
            {{ item.note || '-' }}
          </span>
        </template>

        <!-- Requested At -->
        <template v-slot:item.requested_at="{ item }">
          <small v-if="item.requested_at">{{ item.requested_at | moment("LLL") }}</small>
          <small v-else-if="item.created_at">{{ item.created_at | moment("LLL") }}</small>
          <span v-else>-</span>
        </template>

        <!-- Status -->
        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" dark small class="font-weight-medium">
            {{ getStatusLabel(item.status) }}
          </v-chip>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center">
            <!-- View Details Button -->
            <v-btn
              icon
              color="info"
              small
              @click="viewDetails(item)"
              :title="$t('Details') || 'التفاصيل'"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>

            <!-- Approve Button (Only if Pending) -->
            <v-btn
              v-if="item.status === 'pending'"
              icon
              color="success"
              small
              @click="openActionDialog(item, 'approve')"
              :title="$t('approve') || 'اعتماد'"
            >
              <v-icon>mdi-check-circle</v-icon>
            </v-btn>

            <!-- Reject Button (Only if Pending) -->
            <v-btn
              v-if="item.status === 'pending'"
              icon
              color="error"
              small
              @click="openActionDialog(item, 'reject')"
              :title="$t('reject') || 'رفض'"
            >
              <v-icon>mdi-close-circle</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Details Dialog -->
      <v-dialog v-model="detailsDialog" max-width="650px">
        <v-card v-if="selectedRequest">
          <v-card-title class="headline primary white--text d-flex align-center">
            <v-icon color="white" class="me-2">mdi-file-document-outline</v-icon>
            <span>{{ $t('emergency_request_details') || 'تفاصيل طلب إنهاء الطوارئ' }} #{{ selectedRequest.id }}</span>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-row>
              <!-- Driver Info -->
              <v-col cols="12" sm="6">
                <div class="subtitle-2 text-muted">{{ $t('driver_name') || 'الكابتن' }}</div>
                <div class="body-1 font-weight-bold" v-if="selectedRequest.driver">
                  {{ selectedRequest.driver.name }}
                </div>
                <div class="text-caption text-muted" v-if="selectedRequest.driver">
                  {{ selectedRequest.driver.tel_number }} | {{ selectedRequest.driver.email }}
                </div>
              </v-col>

              <!-- Planned Trip Info -->
              <v-col cols="12" sm="6">
                <div class="subtitle-2 text-muted">{{ $t('trip') || 'الرحلة' }}</div>
                <div class="body-1 font-weight-bold" v-if="selectedRequest.planned_trip">
                  #{{ selectedRequest.planned_trip.id }} - {{ selectedRequest.planned_trip.planned_date }}
                </div>
                <div class="text-caption text-muted" v-if="selectedRequest.planned_trip && selectedRequest.planned_trip.route">
                  {{ selectedRequest.planned_trip.route.name }}
                </div>
              </v-col>

              <!-- Reason -->
              <v-col cols="12" sm="6">
                <div class="subtitle-2 text-muted">{{ $t('reason') || 'السبب' }}</div>
                <v-chip color="orange lighten-5" class="orange--text text--darken-3 font-weight-medium mt-1">
                  {{ getReasonLabel(selectedRequest) }}
                </v-chip>
              </v-col>

              <!-- Status -->
              <v-col cols="12" sm="6">
                <div class="subtitle-2 text-muted">{{ $t('Status') || 'الحالة' }}</div>
                <v-chip :color="getStatusColor(selectedRequest.status)" dark small class="mt-1 font-weight-medium">
                  {{ getStatusLabel(selectedRequest.status) }}
                </v-chip>
              </v-col>

              <!-- Requested At -->
              <v-col cols="12" sm="6">
                <div class="subtitle-2 text-muted">{{ $t('requested_at') || 'تاريخ إرسال الطلب' }}</div>
                <div class="body-1">
                  {{ (selectedRequest.requested_at || selectedRequest.created_at) | moment("LLL") }}
                </div>
              </v-col>

              <!-- Bus Info (If available) -->
              <v-col cols="12" sm="6" v-if="selectedRequest.planned_trip && selectedRequest.planned_trip.bus">
                <div class="subtitle-2 text-muted">{{ $t('bus') || 'وسيلة التنقل' }}</div>
                <div class="body-1">
                  {{ selectedRequest.planned_trip.bus.license }}
                </div>
              </v-col>

              <!-- Note -->
              <v-col cols="12">
                <div class="subtitle-2 text-muted">{{ $t('note') || 'الملاحظة' }}</div>
                <div class="body-1 bg-light rounded p-3 mt-1" style="background-color: #f8f9fa; padding: 12px; border-radius: 6px;">
                  {{ selectedRequest.note || 'لا توجد ملاحظات إضافية' }}
                </div>
              </v-col>

              <!-- Review Info (If Reviewed) -->
              <v-col cols="12" v-if="selectedRequest.status !== 'pending'">
                <v-divider class="my-2"></v-divider>
                <div class="subtitle-2 text-muted font-weight-bold">{{ $t('review_details') || 'تفاصيل المراجعة' }}</div>
                <v-row class="mt-1">
                  <v-col cols="12" sm="6" v-if="selectedRequest.reviewer">
                    <small class="text-muted">{{ $t('reviewed_by') || 'المراجع' }}:</small>
                    <div class="font-weight-medium">{{ selectedRequest.reviewer.name }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" v-if="selectedRequest.reviewed_at">
                    <small class="text-muted">{{ $t('reviewed_at') || 'تاريخ المراجعة' }}:</small>
                    <div>{{ selectedRequest.reviewed_at | moment("LLL") }}</div>
                  </v-col>
                  <v-col cols="12" v-if="selectedRequest.review_note">
                    <small class="text-muted">{{ $t('review_note') || 'ملاحظات المراجعة' }}:</small>
                    <div class="body-2 bg-light rounded p-2" style="background-color: #eef2f5; padding: 8px;">
                      {{ selectedRequest.review_note }}
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="pa-4">
            <!-- Action buttons inside dialog if status is pending -->
            <v-btn
              v-if="selectedRequest.status === 'pending'"
              color="success"
              @click="detailsDialog = false; openActionDialog(selectedRequest, 'approve')"
            >
              <v-icon small class="me-1">mdi-check-circle</v-icon>
              {{ $t('approve') || 'اعتماد' }}
            </v-btn>

            <v-btn
              v-if="selectedRequest.status === 'pending'"
              color="error"
              outlined
              class="ms-2"
              @click="detailsDialog = false; openActionDialog(selectedRequest, 'reject')"
            >
              <v-icon small class="me-1">mdi-close-circle</v-icon>
              {{ $t('reject') || 'رفض' }}
            </v-btn>

            <v-spacer></v-spacer>
            <v-btn color="secondary" text @click="detailsDialog = false">{{ $t('Back') || 'إغلاق' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Action Confirmation Dialog (Approve / Reject) -->
      <v-dialog v-model="actionDialog" max-width="500px">
        <v-card v-if="selectedRequest">
          <v-card-title :class="actionType === 'approve' ? 'success white--text' : 'error white--text'">
            <v-icon color="white" class="me-2">
              {{ actionType === 'approve' ? 'mdi-check-circle' : 'mdi-close-circle' }}
            </v-icon>
            <span>
              {{ actionType === 'approve' ? ($t('approve_request') || 'اعتماد طلب الطوارئ') : ($t('reject_request') || 'رفض طلب الطوارئ') }}
              #{{ selectedRequest.id }}
            </span>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-alert
              :type="actionType === 'approve' ? 'success' : 'error'"
              dense
              text
              class="mb-4"
            >
              {{ actionType === 'approve'
                ? 'سيتم اعتماد طلب إنهاء الطوارئ المقدم من السائق. ملاحظة: هذا الإجراء لا يغير حالة الرحلة تلقائياً.'
                : 'سيتم رفض طلب إنهاء الطوارئ المقدم من السائق.'
              }}
            </v-alert>

            <v-textarea
              v-model="reviewNote"
              :label="$t('review_note_optional') || 'ملاحظات المراجعة (اختياري)'"
              outlined
              rows="3"
              hide-details
            ></v-textarea>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-btn color="grey" text @click="actionDialog = false" :disabled="isSubmitting">
              {{ $t('cancel') || 'إلغاء' }}
            </v-btn>

            <v-spacer></v-spacer>

            <v-btn
              :color="actionType === 'approve' ? 'success' : 'error'"
              :loading="isSubmitting"
              :disabled="isSubmitting"
              @click="submitAction"
            >
              {{ actionType === 'approve' ? ($t('confirm_approve') || 'تأكيد الاعتماد') : ($t('confirm_reject') || 'تأكيد الرفض') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import auth from '@/services/AuthService';

export default {
  name: "DriverEmergencyRequests",
  data() {
    return {
      emergencyRequests: [],
      isLoading: false,
      search: "",
      statusFilter: "pending",
      detailsDialog: false,
      actionDialog: false,
      selectedRequest: null,
      actionType: "approve", // 'approve' or 'reject'
      reviewNote: "",
      isSubmitting: false,
      statusOptions: [
        { text: 'قيد المراجعة (Pending)', value: 'pending' },
        { text: 'تمت الموافقة (Approved)', value: 'approved' },
        { text: 'مرفوض (Rejected)', value: 'rejected' },
        { text: 'ملغي (Cancelled)', value: 'cancelled' },
        { text: 'الكل (All)', value: 'all' },
      ],
      reasonsMap: {
        vehicle_breakdown: 'عطل في المركبة',
        accident: 'حادث',
        medical_emergency: 'حالة صحية طارئة',
        road_block: 'تعطل الطريق',
        passenger_issue: 'مشكلة مع راكب',
        other: 'أخرى',
      },
      headers: [
        { text: "req_id", value: "id", width: "80px" },
        { text: "captain", value: "driver" },
        { text: "trip", value: "planned_trip" },
        { text: "reason", value: "reason" },
        { text: "note", value: "note" },
        { text: "requested_at", value: "requested_at" },
        { text: "Status", value: "status" },
        { text: "Actions", value: "actions", sortable: false, width: "120px" },
      ],
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => {
        let label = header.text;
        if (header.text === 'req_id') label = this.$t('req_id') || 'رقم الطلب';
        if (header.text === 'captain') label = this.$t('captain') || 'الكابتن';
        if (header.text === 'trip') label = this.$t('trip') || 'الرحلة';
        if (header.text === 'reason') label = this.$t('reason') || 'السبب';
        if (header.text === 'note') label = this.$t('note') || 'الملاحظة';
        if (header.text === 'requested_at') label = this.$t('requested_at') || 'تاريخ الطلب';
        if (header.text === 'Status') label = this.$t('Status') || 'الحالة';
        if (header.text === 'Actions') label = this.$t('Actions') || 'الإجراءات';

        return {
          ...header,
          text: label
        };
      });
    },
  },
  mounted() {
    this.loadRequests();
  },
  methods: {
    loadRequests() {
      this.isLoading = true;
      axios
        .get('/driver-emergency-requests', {
          params: {
            status: this.statusFilter,
            search: this.search,
          }
        })
        .then((response) => {
          this.emergencyRequests = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: "خطأ",
            text: "حدث خطأ أثناء جلب طلبات إنهاء الطوارئ",
            type: "error",
          });
          console.error(error);
          if (error.response && error.response.data && error.response.data.message) {
            auth.checkError(error.response.data.message, this.$router, this.$swal);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getAvatar(avatar) {
      if (!avatar) return require('@/assets/images/avatars/1.png');
      if (avatar.startsWith('http')) return avatar;
      return avatar;
    },

    getReasonLabel(item) {
      if (!item) return '-';
      if (item.reason_label) return item.reason_label;
      return this.reasonsMap[item.reason] || item.reason;
    },

    getStatusLabel(status) {
      const labels = {
        pending: 'قيد المراجعة',
        approved: 'تمت الموافقة',
        rejected: 'مرفوض',
        cancelled: 'ملغي',
      };
      return labels[status] || status;
    },

    getStatusColor(status) {
      const colors = {
        pending: 'warning',
        approved: 'success',
        rejected: 'error',
        cancelled: 'secondary',
      };
      return colors[status] || 'primary';
    },

    viewDetails(item) {
      this.selectedRequest = item;
      this.detailsDialog = true;
    },

    openActionDialog(item, type) {
      this.selectedRequest = item;
      this.actionType = type;
      this.reviewNote = "";
      this.actionDialog = true;
    },

    submitAction() {
      if (!this.selectedRequest) return;

      this.isSubmitting = true;
      const endpoint = `/driver-emergency-requests/${this.selectedRequest.id}/${this.actionType}`;

      axios
        .post(endpoint, {
          review_note: this.reviewNote
        })
        .then((response) => {
          this.$notify({
            title: "نجاح",
            text: response.data.message || "تم تحديث حالة الطلب بنجاح",
            type: "success",
          });

          if (response.data && response.data.data) {
            const updated = response.data.data;
            const index = this.emergencyRequests.findIndex(r => r.id === updated.id);
            if (index !== -1) {
              this.$set(this.emergencyRequests, index, updated);
            }
          }

          this.actionDialog = false;
          this.loadRequests();
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.message)
            ? error.response.data.message
            : "حدث خطأ أثناء معالجة الطلب";

          this.$swal({
            title: "خطأ",
            text: msg,
            icon: "error",
            confirmButtonText: "موافق",
          });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
  },
};
</script>

<style scoped>
.max-width-300 {
  max-width: 300px;
}
.max-width-200 {
  max-width: 200px;
}
</style>
